import { Fragment, useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useLazyQuery } from '@apollo/client';
import { GET_VESSELS, GetVesselsOutput } from '../../api';
import { capitalizeFirstLetterEveryword } from '../../shared/stringFunctions';
import { useTranslation } from 'react-i18next';
import { TXT_AND_MORE, TXT_VESSEL } from '../../../../../shared/translations';
import { vesselModel } from '../../model';
import { useApolloErrorHandler } from '../../hooks/useApolloErrorHandler';
import _ from 'lodash';

export interface VesselSelectorProps {
  vessel?: vesselModel;
  error?: boolean;
  disabled?: boolean;
  onSelectedChange?: (id: string | null) => void;
  onChanged?: (vessel?: vesselModel) => void;
}

const VISIBLE_OPTIONS_COUNT = 5;

export const VesselSelector = (props: VesselSelectorProps) => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<vesselModel | null>(props.vessel || null);
  const [text, setText] = useState<string | undefined>(props.vessel?.name);
  const [options, setOptions] = useState<vesselModel[]>([]);

  const { apolloErrorHandler } = useApolloErrorHandler();

  const [getVessels, { data: vesselData, loading, refetch }] = useLazyQuery<GetVesselsOutput>(GET_VESSELS, {
    fetchPolicy: 'network-only',
    variables: {
      name: text,
      take: VISIBLE_OPTIONS_COUNT,
      skip: 0
    },
    onError: apolloErrorHandler
  });

  const fetch = () => {
    if (refetch) {
      refetch();
    } else {
      getVessels();
    }
  };

  const onInputChange = (event: object, value: string) => {
    setText(value);
  };

  const onSelectedChange = (event: object, value: vesselModel | null) => {
    setSelected(value);
    props.onSelectedChange && props.onSelectedChange(value && (value.id as string));
    props.onChanged && props.onChanged(value === null ? undefined : value);
  };

  useEffect(() => {
    fetch();
  }, [text]);

  useEffect(() => {
    if ((vesselData?.vessels?.items?.length || 0) > 0) {
      let vesselOptions: vesselModel[] = (vesselData?.vessels?.items || []).map((vessel) => ({ id: vessel.id, name: vessel.name }));
      const totalCount = vesselData?.vessels?.totalCount || 0;
      if (totalCount > VISIBLE_OPTIONS_COUNT) {
        vesselOptions = [
          ...vesselOptions,
          {
            id: 'vessel-hidden-options-remaining',
            name: capitalizeFirstLetterEveryword(t(TXT_AND_MORE, {
              count: totalCount - VISIBLE_OPTIONS_COUNT
            }))
          }
        ];
      }
      if (!_.isNil(selected)) {
        vesselOptions = [{ ...selected }, ...vesselOptions];
      }
      setOptions(_.uniqBy(vesselOptions, 'id'));
    }
  }, [vesselData]);

  useEffect(() => {
    setSelected(() => props.vessel || null);
  }, [props.vessel]);

  return (
    <Autocomplete
      id="vessel-selector"
      onChange={onSelectedChange}
      onInputChange={onInputChange}
      value={selected}
      getOptionDisabled={(option) => option.id === 'vessel-hidden-options-remaining'}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name || ''}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={capitalizeFirstLetterEveryword(t(TXT_VESSEL))}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Fragment>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </Fragment>
            )
          }}
          error={props.error}
        />
      )}
      disabled={props.disabled}
    />
  );
};
